import { cn } from "@/components/podkit/lib/cn";
import type React from "react";
import { useMemo } from "react";

export type TextShimmerProps = {
    children: string;
    className?: string;
    duration?: number;
    spread?: number;
};

/**
 * This adds a shimmer effect to the text.
 *
 * The implementation is based on the following component:
 * https://github.com/ibelick/motion-primitives/blob/main/components/core/text-shimmer.tsx
 *
 * With the following changes:
 * - We don't use Motion (motion.dev) yet so we're relying on a custom Tailwind animation to
 *   animate the background position.
 * - Colors have been updated to match our color tokens.
 */

export function TextShimmer({ children, className, spread = 2 }: TextShimmerProps) {
    const dynamicSpread = useMemo(() => {
        return children.length * spread;
    }, [children, spread]);

    return (
        <p
            className={cn(
                "relative inline-block bg-[length:250%_100%,auto] bg-clip-text",
                "animate-text-shimmer",
                "text-transparent",
                "[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),rgb(var(--content-primary)),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
                "dark:[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),rgb(var(--content-primary)),#0000_calc(50%+var(--spread)))]",
                className,
            )}
            style={
                {
                    "--spread": `${dynamicSpread}px`,
                    backgroundImage: `var(--bg), linear-gradient(rgb(var(--content-tertiary)), rgb(var(--content-tertiary)))`,
                } as React.CSSProperties
            }
        >
            {children}
        </p>
    );
}
